import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, ShowerHeadIcon, BathIcon, ChevronDownIcon, DropletsIcon, WrenchIcon } from 'lucide-react';
import { BathsRUsQuoteForm } from './BathsRUsQuoteForm';
interface BathsRUsLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function BathsRUsLandingPage({
  initialService,
  onClose
}: BathsRUsLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <BathsRUsQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-415" />;
  }
  const services = [{
    id: 'tub-to-shower',
    name: 'Tub-to-Shower Conversion',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-416" />,
    desc: 'Modernize your bathroom'
  }, {
    id: 'walk-in-tub',
    name: 'Walk-in Tub',
    icon: <BathIcon className="w-6 h-6" data-id="element-417" />,
    desc: 'Safe & accessible bathing'
  }, {
    id: 'shower-replacement',
    name: 'Shower Replacement',
    icon: <DropletsIcon className="w-6 h-6" data-id="element-418" />,
    desc: 'Upgrade your daily shower'
  }, {
    id: 'bath-remodel',
    name: 'Full Bath Remodel',
    icon: <WrenchIcon className="w-6 h-6" data-id="element-419" />,
    desc: 'Complete bathroom transformation'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-420">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-421">
        <div className="flex flex-col items-center" data-id="element-422">
          <div className="flex items-baseline gap-2" data-id="element-423">
            <img src="https://cdn.magicpatterns.com/uploads/ww2rdwZ9zBytie27eikQmU/Baths_r_us_lgo.webp" alt="Baths R Us" className="h-14 md:h-16 object-contain" data-id="element-424" />
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-425">
            Tub-to-Shower &bull; Walk-in Tubs &bull; Shower Replacement &bull;
            Bath Remodel
          </span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#1E6CB6] py-4 md:py-6 border-t border-[#155a9c]" data-id="element-426">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-427">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-428">
            Your Dream Bathroom, Made Simple
          </h2>
          <p className="text-blue-100 text-sm mt-1" data-id="element-429">
            Serving homeowners nationwide with excellence.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-430">
        <div className="absolute inset-0 z-0" data-id="element-431">
          <img src="https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80" alt="Beautiful modern bathroom" className="w-full h-full object-cover" data-id="element-432" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-433" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-434">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-435">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-436">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-437">
                Your Dream Bathroom, Made Simple
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-438">
                Get a free estimate from your local bathroom remodeling experts.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-439">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-440">
                <h2 className="text-xl font-bold text-[#1E6CB6] text-center mb-6" data-id="element-441">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {/* Service dropdown */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-442">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-blue-50 ${selectedService ? 'border-[#1E6CB6]' : 'border-blue-200'} ${dropdownOpen ? 'border-[#1E6CB6] shadow-md' : 'hover:border-blue-300'}`} data-id="element-443">
                      <div className="flex items-center gap-3" data-id="element-444">
                        {selectedService ? <>
                            <span className="text-[#1E6CB6]" data-id="element-445">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#1E6CB6] text-lg" data-id="element-446">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-447">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#1E6CB6] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-448" />
                    </button>

                    <AnimatePresence data-id="element-449">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-blue-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-450">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-blue-50 text-[#1E6CB6]' : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-[#1E6CB6]'} border-b border-blue-100 last:border-b-0`} data-id="element-451">
                              <span className={`${selectedService === svc.id ? 'text-[#1E6CB6]' : 'text-gray-400'}`} data-id="element-452">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-453">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#1E6CB6] ml-auto" data-id="element-454" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-id="element-455">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#1E6CB6]" data-id="element-456">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-457">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#1E6CB6] focus:ring-1 focus:ring-[#1E6CB6] transition-colors" maxLength={5} data-id="element-458" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-500" data-id="element-459">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-460" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-461">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-462">
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-463">
                    <ShieldCheckIcon className="w-5 h-5 text-blue-600" data-id="element-464" />
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-465">
                      Licensed & Insured
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200" data-id="element-466">
                    <div className="font-black text-slate-700 text-sm" data-id="element-467">BBB</div>
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-tight" data-id="element-468">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blue Accent Bar */}
      <div className="w-full h-3 bg-[#1E6CB6]" data-id="element-469" />

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-470">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-471">
          <div className="text-center mb-16" data-id="element-472">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-473">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-474">
            {[{
            title: 'Choose Your Service',
            desc: 'Select from tub-to-shower conversions, walk-in tubs, or full bath remodels. Our experts specialize in transforming your space.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Get Your Free Estimate',
            desc: 'A Baths R Us representative will visit your home, assess your bathroom, review options and pricing, and provide a no-obligation quote.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Professional Installation',
            desc: 'Our factory-trained installers handle everything from start to finish, often completing projects in as little as one day.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-475">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-476">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-477" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-478">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-479">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#FAFAFA]" data-id="element-480">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-481">
          <div className="text-center mb-12" data-id="element-482">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-483">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-484">
            {[{
            name: 'Linda M.',
            location: 'Cleveland, OH',
            text: 'Baths R Us transformed my old, outdated tub into a beautiful walk-in shower. The crew was fast, clean, and very professional. I love my new bathroom!'
          }, {
            name: 'James & Mary K.',
            location: 'Pittsburgh, PA',
            text: 'We needed a walk-in tub for safety reasons. The installation was seamless, and the quality of the tub is outstanding. Highly recommend their services.'
          }, {
            name: 'Robert W.',
            location: 'Indianapolis, IN',
            text: 'From the initial consultation to the final installation, everything was perfect. They helped us choose the right design and finished the job in just two days.'
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-485">
                <div className="flex text-yellow-400 mb-4" data-id="element-486">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-487" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-488">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div data-id="element-489">
                  <p className="font-bold text-gray-900" data-id="element-490">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-491">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E6CB6] py-16" data-id="element-492">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-493">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-494">
            <div className="flex flex-col gap-8" data-id="element-495">
              <div className="flex flex-col" data-id="element-496">
                <img src="https://cdn.magicpatterns.com/uploads/ww2rdwZ9zBytie27eikQmU/Baths_r_us_lgo.webp" alt="Baths R Us" className="h-12 object-contain self-start bg-white px-3 py-1.5 rounded-lg mb-2" data-id="element-497" />
                <span className="text-[10px] font-semibold text-white/90" data-id="element-498">
                  Tub-to-Shower &bull; Walk-in Tubs &bull; Shower Replacement
                  &bull; Bath Remodel
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-499">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-500">
              <div className="flex flex-col gap-4" data-id="element-501">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-502">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-503">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-504">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-505">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-506">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-507">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-508">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-509">
              Baths R Us services are available nationwide. All estimates are
              free and no-obligation. Actual project costs may vary based on
              scope, materials, and local conditions. Licensed and insured.
              Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}